import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { usePricingStack } from "./UsePricingStack";
import { FINANCE_PRODUCT_TYPE } from "../../common/Constants";

export function usePaymentTypeDetails(orderId) {
  const queryClient = useQueryClient();
  const getPricingStack = usePricingStack(orderId);

  const getPaymentTypeDetails = useCallback(async () => {
    try {
      const { customerReservation } = queryClient.getQueryData([
        "order",
        { orderId },
      ]);

      const {
        spotBuy,
        financeType,
        financeDataQuote,
        paymentType,
        financeProvider,
        purchaseDeposit,
        pricingStack: reservationPricingStack,
        paymentDescription,
      } = customerReservation;

      if (spotBuy) {
        return { spotBuy };
      }

      const { data: pricingStackData } = await getPricingStack();
      const {
        totalPriceWithAddOns,
        monthlyUseTax,
        residualData,
        capitalizedCostReduction,
        availableIncentivesAndNonCashCredits,
        totalAmountDueAtSigning,
        taxesAndFees: pricingStackTaxesAndFees,
      } = pricingStackData;

      const {
        deposit: downPayment,
        apr,
        monthlyPayment,
        term,
        totalAmountFinancedStandard,
        amountFinanced,
        estimatedBasePayment,
        baseMonthlyPayment,
        annualMileage,
        totalAmountDueAtSigning: dataQuoteTotalAmountDueAtSigning,
      } = financeDataQuote;

      const { tradeInPrice, cashDueAtSigning } = reservationPricingStack;
      const totalTaxesAndFees =
        pricingStackTaxesAndFees?.totalEstimatedTaxAndFees ??
        reservationPricingStack?.taxesAndFees?.totalEstimatedTaxAndFees;

      let purchaseDepositAmountInt;

      if (purchaseDeposit) {
        ({ depositAmountInt: purchaseDepositAmountInt } = purchaseDeposit);
      }

      if (
        paymentType.toLowerCase() === FINANCE_PRODUCT_TYPE.FINANCE &&
        financeProvider
      ) {
        return {
          financeType,
          totalAmountFinancedStandard:
            totalAmountFinancedStandard || amountFinanced,
          downPayment,
          apr,
          monthlyPayment,
          term,
          taxesAndFees: totalTaxesAndFees,
        };
      }

      if (paymentType === FINANCE_PRODUCT_TYPE.THIRD_PARTY) {
        return {
          financeType,
          statusFromPaymentDescription: paymentDescription[0]?.status,
        };
      }

      if (
        paymentType.toLowerCase() ===
          FINANCE_PRODUCT_TYPE.THIRD_PARTY_OPTIONS &&
        financeProvider
      ) {
        return {
          financeType,
          totalPriceWithAddOns,
          downPayment,
          apr,
          monthlyPayment,
          term,
          totalAmountFinancedStandard,
        };
      }

      if (paymentType.toLowerCase() === FINANCE_PRODUCT_TYPE.CASH) {
        return {
          ...(financeType && { financeType }),
          ...(purchaseDepositAmountInt && { purchaseDepositAmountInt }),
          ...(totalPriceWithAddOns && { totalPriceWithAddOns }),
        };
      }

      if (paymentType.toLowerCase() === FINANCE_PRODUCT_TYPE.OTHER) {
        return {
          financeType,
          totalPriceWithAddOns,
          downPayment,
          apr,
          monthlyPayment,
          term,
          totalAmountFinancedStandard,
          taxesAndFees: totalTaxesAndFees,
        };
      }

      if (paymentType.toLowerCase() === FINANCE_PRODUCT_TYPE.LEASE) {
        return {
          baseMonthlyPayment,
          estimatedBasePayment,
          financeType,
          monthlyUseTax,
          annualMileage,
          residualData,
          capitalizedCostReduction,
          downPayment,
          monthlyPayment,
          term,
          availableIncentivesAndNonCashCredits,
          tradeInPrice,
          cashDueAtSigning,
          totalAmountDueAtSigning,
          taxesAndFees: totalTaxesAndFees,
          dataQuoteTotalAmountDueAtSigning,
        };
      }
    } catch (error) {
      console.error("Error fetching payment type details:", error);
      throw error;
    }
  }, [orderId, queryClient, getPricingStack]);

  return getPaymentTypeDetails;
}
